import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

/** What a form needs to render the leave prompt: see UnsavedChangesDialog. */
export interface UnsavedChangesControl {
  /** True while a navigation is waiting on an answer. */
  blocked: boolean;
  /** Cancel the navigation and keep the edits. */
  stay: () => void;
  /** Let the navigation go ahead and lose the edits. */
  leave: () => void;
}

/**
 * Every mounted form with unsaved edits, whichever component holds it.
 *
 * The router consults one blocker per app - with several mounted it takes the
 * last - so a page holding several forms (the staff organization page has
 * three) cannot rely on each form's own flag: the consulted blocker might be a
 * clean form's. Each instance instead asks "is any form dirty", so whichever
 * one the router consults answers for the whole page, and its dialog is the
 * one that opens. The prompt is the same words wherever it comes from.
 */
const dirtyForms = new Set<symbol>();

/**
 * Holds every way out of a form with unsaved edits until the person answers.
 *
 * Called beside the `isDirty` the Save button already reads, with that same
 * boolean, so a form cannot disagree with its own prompt. Two exits exist and
 * each needs its own guard:
 *
 * - Moving inside the portal goes through the router, so `useBlocker` pauses
 *   the navigation while `blocked` is true and the dialog asks. Only a change
 *   of pathname counts: a filter or a tab writing to the same page's search
 *   must never prompt.
 * - Reloading or closing the tab never reaches the router. The `beforeunload`
 *   listener asks the browser to show its own dialog (its words cannot be
 *   chosen). The listener exists only while the form is dirty, because its
 *   mere presence stops the browser putting the page in its back-forward
 *   cache.
 */
export function useUnsavedChanges(dirty: boolean): UnsavedChangesControl {
  const id = useRef<symbol | null>(null);
  id.current ??= Symbol('unsaved form');

  useEffect(() => {
    if (!dirty) return;
    const key = id.current!;
    dirtyForms.add(key);
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      dirtyForms.delete(key);
      window.removeEventListener('beforeunload', warn);
    };
  }, [dirty]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirtyForms.size > 0 && currentLocation.pathname !== nextLocation.pathname,
  );

  return {
    blocked: blocker.state === 'blocked',
    stay: () => {
      if (blocker.state === 'blocked') blocker.reset();
    },
    leave: () => {
      if (blocker.state === 'blocked') blocker.proceed();
    },
  };
}
